/**
 * The dashboard's preparation: what runs once on the data after RESET and
 * before any tile. The chosen saved pipelines' scripts in the order the
 * dashboard lists them, then the weight, then the hand-written script kept
 * under Advanced. Pure: the pipelines come in already loaded.
 */

import type { HierarchicalDataEntry } from "types/engineOutput.types"

/** A saved pipeline as the preparation needs it: its name and its script. */
export interface PrepPipeline {
  name: string
  script: string
}

/** One part of the preparation, run on its own so a failure can name it. */
export interface PrepPart {
  /** What a failure names: the pipeline, the weight line, or the Advanced script. */
  label: string
  script: string
}

interface PrepInput {
  pipelines: PrepPipeline[]
  chosen: string[]
  weight: string | null
  advanced: string
  /** Names the Conductor already ran on load: skipped, so none runs twice. */
  onLoad?: string[]
}

/**
 * The engine line that weights every later table by `weight`, or clears the
 * weight when there is none. The weight is a codebook path, so it goes
 * between `<<` and `>>` like any variable.
 */
export const weightsLine = (weight: string | null): string =>
  weight ? `USEWEIGHTS <<${weight}>>` : "USEWEIGHTS"

/** `script` without blank lines before its first line or anything after its last. */
const trimLines = (script: string): string =>
  script.replace(/^(?:[ \t]*\r?\n)+/, "").trimEnd()

/**
 * The preparation in the order it runs: each chosen pipeline's script (a
 * name the project no longer has is skipped and reported in `missing`; an
 * empty script is left out; a name in `onLoad` is skipped silently, since
 * the Conductor ran it on load), then `USEWEIGHTS <<weight>>` when a weight
 * is set, then the Advanced script when it is not blank.
 */
export const prepParts = (
  args: PrepInput
): { parts: PrepPart[]; missing: string[] } => {
  const byName = new Map(args.pipelines.map((p) => [p.name, p.script]))
  const ranOnLoad = new Set(args.onLoad ?? [])
  const parts: PrepPart[] = []
  const missing: string[] = []
  args.chosen.forEach((name) => {
    if (ranOnLoad.has(name)) return
    const script = byName.get(name)
    if (script === undefined) {
      missing.push(name)
      return
    }
    const text = trimLines(script)
    if (text) parts.push({ label: `Pipeline "${name}"`, script: text })
  })
  if (args.weight) {
    const line = weightsLine(args.weight)
    parts.push({ label: line, script: line })
  }
  const advanced = args.advanced.trim()
  if (advanced) parts.push({ label: "Advanced script", script: advanced })
  return { parts, missing }
}

/**
 * The preparation as one script: its parts (see `prepParts`) one after
 * another. Nothing to run gives "".
 */
export const assemblePrepScript = (
  args: PrepInput
): { script: string; missing: string[] } => {
  const { parts, missing } = prepParts(args)
  return { script: parts.map((p) => p.script).join("\n"), missing }
}

/** Paths of the codebook's numeric variables (INT or REAL), in codebook order. */
export const numericVariables = (
  entries: HierarchicalDataEntry[] | null,
  parent = ""
): string[] =>
  (entries ?? []).flatMap((entry) => {
    if (!entry || typeof entry !== "object") return []
    const path = parent ? `${parent}//${entry.label}` : String(entry.label)
    if (entry.type === "INT" || entry.type === "REAL") return [path]
    return entry.type === "TABLE" && Array.isArray(entry.value)
      ? numericVariables(entry.value as HierarchicalDataEntry[], path)
      : []
  })
